import React, { useState } from "react";
import styled from "@emotion/styled";

import OrganisationsPopover from "components/record_details/OrganisationsPopover";
import DisplayDate from "components/ui/date/DisplayDate";
import { OpportunityDetails } from "../../../lib/hooks/api/opportunities/useOpportunity";
import { OpportunityEntityType } from "../../../lib/types/graphQLEnums";
import { Flex, Text } from "../../../styles/utility-components";
import DocumentDrawer from "../../document_details/DocumentDrawer";
import { DEFAULT_DOCUMENT_FILTERS, getDocumentTypeLabel } from "../../documents/utils";
import { opportunitySavedCardContainer } from "./shared/styles";
import EntityCardContent from "./EntityCardContent";
import Property from "./EntityProperty";
import FindEntity from "./FindEntity";

type Props = {
  documents: OpportunityDetails["documents"];
  entityUrl: string;
  opportunityId: string;
};

export default function OpportunityDocuments({ documents, entityUrl, opportunityId }: Props) {
  const [selectedDocumentId, setSelectedDocumentId] = useState<string>();

  const handleViewDocument = (id: string) => () => {
    setSelectedDocumentId(id);
  };

  const onOpenChange = (open: boolean) => {
    if (!open) {
      setSelectedDocumentId(undefined);
    } else {
      console.warn("onOpenChange with open=true is unsupported in OpportunityDocuments");
    }
  };

  return (
    <Flex column gap={16}>
      {documents.map((document) => (
        <DocumentCard
          key={document.id}
          document={document}
          onView={handleViewDocument(document.id)}
          opportunityId={opportunityId}
        />
      ))}
      <FindEntity entity="document" url={entityUrl} isEmptyState={!documents.length} />
      <DocumentDrawer
        open={!!selectedDocumentId}
        onOpenChange={onOpenChange}
        documentId={selectedDocumentId ?? undefined}
        filters={DEFAULT_DOCUMENT_FILTERS}
        disablePermissionCheck
      />
    </Flex>
  );
}

function DocumentCard({
  document,
  onView,
  opportunityId,
}: {
  document: OpportunityDetails["documents"][0];
  onView: () => void;
  opportunityId: string;
}) {
  return (
    <Container
      key={document.id}
      onClick={() => {
        onView();
      }}
    >
      <EntityCardContent
        header={<Text fontWeight="600">{document.title || "Untitled Document"}</Text>}
        entity="document"
        entityId={document.id}
        entityType={OpportunityEntityType.Document}
        entityName={document.title ?? "Untitled Document"}
        opportunityId={opportunityId}
      >
        <Flex column gap={16} width="100%">
          <Property
            title="Document type"
            value={getDocumentTypeLabel(document.category ?? "")}
            icon="box"
          />
          <Property
            title="Buyer(s)"
            value={<OrganisationsPopover orgType="buyers" orgs={document.buyers ?? []} />}
            icon="building08"
          />
          <Property
            title="Publish date"
            value={
              document.publishedAt ? <DisplayDate value={new Date(document.publishedAt)} /> : null
            }
            icon="calendar"
          />
        </Flex>
      </EntityCardContent>
    </Container>
  );
}

const Container = styled(Flex)(({ theme }) => ({
  ...opportunitySavedCardContainer(theme),
}));
